import React, { useMemo } from 'react';

import styled from '@emotion/styled';
import { CircularProgress } from '@mui/material';
import { Blockchain } from '@repo/prsn-blockchain';

import {
  BcBaseIcon,
  BcEthIcon,
  CoinIcon,
  ErrorIcon,
  SuccessIcon,
} from '@web/ui/icons';

import BoostIcon from '../assets/boost.svg';
import UnstakeIcon from '../assets/unstake.svg';
import { IBaseStakingModalProps } from '../BaseStakingModal';

const StakingStepContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing('1500')};
  gap: ${({ theme }) => theme.spacing('1000')};
  border-radius: ${({ theme }) => theme.shape.corner['0750']};
  background: ${({ theme }) => theme.color.surfaceContainerLow};
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing('0500')};
`;

const StepIconContainer = styled.div`
  display: flex;
  align-items: center;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing('0500')};
`;

const StatusIcon = styled.div`
  display: flex;
  align-items: center;
`;

const Title = styled.span`
  color: ${({ theme }) => theme.color.onSurface};
  font: ${({ theme }) => theme.typography.body.bold};
  font-size: ${({ theme }) => theme.typescale.size['0875']};
  letter-spacing: ${({ theme }) => theme.typescale.tracking.default};
  line-height: ${({ theme }) => theme.typescale.lineHeight.extended};
`;

const Description = styled.p`
  color: ${({ theme }) => theme.color.onSurfaceVariant};
  font: ${({ theme }) => theme.typography.body.regular};
  font-size: ${({ theme }) => theme.typescale.size['0875']};
  line-height: ${({ theme }) => theme.typescale.lineHeight.extended};
`;

export interface IStakingStepProps {
  title: string;
  description: string;
  status: IBaseStakingModalProps['status'];
  type: 'claim' | 'stake' | 'unstake' | 'boost' | Blockchain;
}

export const StakingStep = ({
  description,
  status,
  title,
  type,
}: IStakingStepProps) => {
  const stepIcon = useMemo(() => {
    switch (type) {
      case Blockchain.BASE:
        return <BcBaseIcon size="2000" />;
      case Blockchain.ETHEREUM:
        return <BcEthIcon size="2000" />;
      case 'boost':
        return <BoostIcon size="2000" />;
      case 'unstake':
        return <UnstakeIcon size="2000" />;
      default:
        return <CoinIcon size="2000" />;
    }
  }, [type]);

  const statusIcon = useMemo(() => {
    switch (status) {
      case 'success':
        return <SuccessIcon size="1250" />;
      case 'error':
        return <ErrorIcon size="1250" />;
      case 'pending':
        return <CircularProgress size={20} />;
      case 'idle':
      default:
        return null;
    }
  }, [status]);

  return (
    <StakingStepContainer>
      <LeftColumn>
        <TitleContainer>
          {statusIcon && <StatusIcon>{statusIcon}</StatusIcon>}
          <Title>{title}</Title>
        </TitleContainer>
        <Description>{description}</Description>
      </LeftColumn>
      <StepIconContainer>{stepIcon}</StepIconContainer>
    </StakingStepContainer>
  );
};
